/**
 * Fancy Game: parse `rules` text; option buttons cloned from `<template data-fancy-game-option-template>` in Liquid.
 *
 * Two formats are supported (auto-detected):
 *
 * 1) TREE (decision tree) — preferred. Each answer leads to a different next node or a result page.
 *    Optional first line `START <id>` sets the entry node (default: first NODE).
 *    A node block:
 *      NODE <id> | domanda?
 *      etichetta opzione 1 | <targetId | /pages/handle>
 *      etichetta opzione 2 | <targetId | /pages/handle>
 *    Option target: a bare word = id of the next NODE; a path starting with `/` or `http` = redirect (result).
 *    Blank lines separate nodes (a line starting with `NODE ` also starts a new node).
 *
 * 2) LEGACY (fixed steps + combination map) — kept for backwards compatibility:
 *      STEP n | domanda | opzione 1 | opzione 2 | ...   (≥2 options, max 26 per step → keys a–z)
 *      ---
 *      abc=/pages/handle                                (chosen option key per step)
 */
(function () {
  'use strict';

  /** @type {number} Max options per step/node (display letters A–Z). */
  var MAX_OPTIONS = 26;

  var SELECTOR_ROOT = '[data-fancy-game-root]';
  var SELECTOR_PAYLOAD = '[data-fancy-game-payload]';
  var SELECTOR_SHELL = '[data-fancy-game-step-shell]';
  var SELECTOR_QUESTION = '[data-fancy-game-question]';
  var SELECTOR_OPTIONS = '[data-fancy-game-options]';
  var SELECTOR_OPTION_TEMPLATE = '[data-fancy-game-option-template]';
  var SELECTOR_STEP_CONTENT = '[data-fancy-game-step-content]';
  var SELECTOR_ERROR = '[data-fancy-game-error]';

  /**
   * Detects which format the rules text uses.
   * @param {string[]} lines
   * @returns {'tree'|'legacy'}
   */
  function detectMode(lines) {
    for (var i = 0; i < lines.length; i++) {
      if (/^NODE\s+/i.test(lines[i])) return 'tree';
      if (/^STEP\s+/i.test(lines[i])) return 'legacy';
    }
    return 'tree';
  }

  /**
   * Parses the TREE format into a node map.
   * @param {string[]} lines
   * @returns {{ mode: 'tree', start: string, nodes: Object<string, { id: string, question: string, options: Array<{ key: string, letter: string, label: string, target: string }> }> }}
   */
  function parseTree(lines) {
    var nodes = {};
    var order = [];
    var explicitStart = '';
    var current = null;

    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];

      var startMatch = /^START\s+(.+)$/i.exec(line);
      if (startMatch) {
        explicitStart = startMatch[1].trim();
        continue;
      }

      var nodeMatch = /^NODE\s+([^|]+)\|(.*)$/i.exec(line);
      if (nodeMatch) {
        var id = nodeMatch[1].trim();
        current = { id: id, question: nodeMatch[2].trim(), options: [] };
        nodes[id] = current;
        order.push(id);
        continue;
      }

      // Option line: `label | target` belonging to the current node.
      if (current && line.indexOf('|') !== -1) {
        if (current.options.length >= MAX_OPTIONS) continue;
        var sep = line.lastIndexOf('|');
        var label = line.slice(0, sep).trim();
        var target = line.slice(sep + 1).trim();
        if (!label || !target) continue;
        var idx = current.options.length;
        current.options.push({
          key: String.fromCharCode(97 + idx),
          letter: String.fromCharCode(65 + idx),
          label: label,
          target: target,
        });
      }
    }

    var start = explicitStart && nodes[explicitStart] ? explicitStart : order[0];
    return { mode: 'tree', start: start, nodes: nodes };
  }

  /**
   * Parses the LEGACY format (fixed steps + combination → route map).
   * @param {string[]} lines
   * @returns {{ mode: 'legacy', steps: Array<{ index: number, question: string, options: Array<{ key: string, letter: string, label: string }> }>, routes: Object<string, string> }}
   */
  function parseLegacy(lines) {
    var sepIdx = lines.findIndex(function (l) {
      return l === '---';
    });
    var stepLines = sepIdx >= 0 ? lines.slice(0, sepIdx) : lines.filter(function (l) {
      return /^STEP\s+/i.test(l);
    });
    var mapLines = sepIdx >= 0 ? lines.slice(sepIdx + 1) : [];

    var steps = [];
    for (var i = 0; i < stepLines.length; i++) {
      var line = stepLines[i];
      if (!/^STEP\s+/i.test(line)) continue;
      var parts = line.split('|').map(function (p) {
        return p.trim();
      });
      if (parts.length < 4) continue;
      var stepMatch = /^STEP\s+(\d+)/i.exec(parts[0]);
      if (!stepMatch) continue;
      var labels = parts.slice(2);
      if (labels.length < 2) continue;
      if (labels.length > MAX_OPTIONS) labels = labels.slice(0, MAX_OPTIONS);
      var options = [];
      for (var oi = 0; oi < labels.length; oi++) {
        options.push({
          key: String.fromCharCode(97 + oi),
          letter: String.fromCharCode(65 + oi),
          label: labels[oi],
        });
      }
      steps.push({ index: parseInt(stepMatch[1], 10), question: parts[1], options: options });
    }
    steps.sort(function (a, b) {
      return a.index - b.index;
    });

    var routes = {};
    for (var j = 0; j < mapLines.length; j++) {
      var eq = mapLines[j].indexOf('=');
      if (eq <= 0) continue;
      var key = mapLines[j].slice(0, eq).trim().toLowerCase();
      var handle = mapLines[j].slice(eq + 1).trim();
      if (key && handle) routes[key] = handle;
    }

    return { mode: 'legacy', steps: steps, routes: routes };
  }

  /**
   * @param {string} text
   */
  function parseRules(text) {
    var lines = text
      .split(/\r?\n/)
      .map(function (l) {
        return l.trim();
      })
      .filter(Boolean);
    return detectMode(lines) === 'tree' ? parseTree(lines) : parseLegacy(lines);
  }

  /**
   * Renders a question + its option buttons. `targets` (optional) carries each option's
   * tree target, written onto the button as `data-fancy-game-target`.
   * @param {HTMLElement} root
   * @param {{ question: string, options: Array<{ key: string, letter: string, label: string, target?: string }> }} step
   */
  function renderStep(root, step) {
    var q = root.querySelector(SELECTOR_QUESTION);
    var group = root.querySelector(SELECTOR_OPTIONS);
    var tpl = root.querySelector(SELECTOR_OPTION_TEMPLATE);
    if (!q || !group || !tpl) return;

    q.textContent = step.question;
    group.innerHTML = '';

    for (var i = 0; i < step.options.length; i++) {
      var opt = step.options[i];
      var frag = tpl.content.cloneNode(true);
      var btn = frag.querySelector('[data-fancy-game-choice]');
      var letterEl = frag.querySelector('[data-fancy-game-option-letter]');
      var labelEl = frag.querySelector('[data-fancy-game-option-label]');
      if (!btn || !labelEl) continue;
      btn.setAttribute('data-fancy-game-choice', opt.key);
      if (opt.target) btn.setAttribute('data-fancy-game-target', opt.target);
      if (letterEl) letterEl.textContent = opt.letter;
      labelEl.textContent = opt.label;
      btn.setAttribute('aria-label', opt.letter + ': ' + opt.label);
      group.appendChild(frag);
    }
    group.setAttribute('aria-label', step.question);
  }

  /**
   * @param {HTMLElement} root
   */
  function initRoot(root) {
    if (root.getAttribute('data-fancy-game-initialized') === 'true') return;
    var payloadEl = root.querySelector(SELECTOR_PAYLOAD);
    var shell = root.querySelector(SELECTOR_SHELL);
    if (!payloadEl || !shell) return;

    var text;
    try {
      text = JSON.parse(payloadEl.textContent.trim());
    } catch (e) {
      return;
    }
    if (typeof text !== 'string' || !text.trim()) return;

    var parsed = parseRules(text);

    var hasContent = parsed.mode === 'tree'
      ? !!(parsed.start && parsed.nodes[parsed.start])
      : parsed.steps.length > 0;
    if (!hasContent) return;

    root.setAttribute('data-fancy-game-initialized', 'true');
    payloadEl.remove();

    var rootUrl = (root.getAttribute('data-fancy-game-root-url') || '').replace(/\/$/, '');
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var stepContent = root.querySelector(SELECTOR_STEP_CONTENT);
    var errEl = root.querySelector(SELECTOR_ERROR);

    // Legacy state.
    var choices = [];
    var stepIndex = 0;
    // Tree state.
    var currentNodeId = parsed.start;

    /**
     * @param {function(): void} fn
     */
    function animateThen(fn) {
      if (reduceMotion) {
        fn();
        return;
      }
      shell.classList.add('fancy-game__step-shell--out');
      window.setTimeout(function () {
        fn();
        shell.classList.remove('fancy-game__step-shell--out');
        shell.classList.add('fancy-game__step-shell--in');
        window.setTimeout(function () {
          shell.classList.remove('fancy-game__step-shell--in');
        }, 420);
      }, 280);
    }

    function showError(message) {
      if (stepContent) stepContent.classList.add('tw-hidden');
      if (errEl) {
        errEl.textContent = message;
        errEl.classList.remove('tw-hidden');
      }
    }

    function isRedirect(target) {
      return /^(https?:)?\//i.test(target);
    }

    function goTo(target) {
      if (/^https?:/i.test(target)) {
        window.location.href = target;
        return;
      }
      var url = target.charAt(0) === '/' ? rootUrl + target : rootUrl + '/' + target;
      window.location.href = url;
    }

    function handleTreeChoice(btn) {
      var target = btn.getAttribute('data-fancy-game-target');
      if (!target) return;
      if (isRedirect(target)) {
        goTo(target);
        return;
      }
      if (parsed.nodes[target]) {
        currentNodeId = target;
        animateThen(function () {
          renderStep(root, parsed.nodes[currentNodeId]);
        });
      } else {
        animateThen(function () {
          showError('Configurazione del gioco incompleta. Riprova o contatta lo store.');
        });
      }
    }

    function handleLegacyChoice(key) {
      choices.push(key);
      stepIndex += 1;
      if (stepIndex < parsed.steps.length) {
        animateThen(function () {
          renderStep(root, parsed.steps[stepIndex]);
        });
      } else {
        var path = parsed.routes[choices.join('')];
        if (path) {
          goTo(path);
        } else {
          animateThen(function () {
            showError('Nessun risultato corrisponde alle tue scelte. Riprova o contatta lo store.');
          });
        }
      }
    }

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-fancy-game-choice]');
      if (!btn || !root.contains(btn)) return;
      if (parsed.mode === 'tree') {
        handleTreeChoice(btn);
      } else {
        var key = btn.getAttribute('data-fancy-game-choice');
        if (!key || !/^[a-z]$/.test(key)) return;
        handleLegacyChoice(key);
      }
    });

    renderStep(root, parsed.mode === 'tree' ? parsed.nodes[parsed.start] : parsed.steps[0]);
  }

  function onReady() {
    document.querySelectorAll(SELECTOR_ROOT).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
